/**
 * 🏗️ BUILDINGS INDEX
 * 
 * Master export for all buildings across all tiers.
 * Import individual tiers for specific needs, or use the combined `buildings` object.
 * 
 * SCALING (2.5x cost, 1.6x production per tier):
 * 
 * | Tier | Cost Range    | Prod Range | ROI Target |
 * |------|---------------|------------|------------|
 * |  1   | 50-100        | 0.4-0.6/s  | ~60s       |
 * |  2   | 125-250       | 0.65-1/s   | ~90s       |
 * |  3   | 300-600       | 1-1.5/s    | ~2 min     |
 * |  4   | 750-1,500     | 1.6-2.5/s  | ~3 min     |
 * |  5   | 2K-4K         | 2.5-4/s    | ~5 min     |
 * |  6   | 5K-12K        | 4-5.5/s    | ~8 min     |
 * |  7   | 12K-35K       | 6-17/s     | ~12 min    |
 * |  8   | 35K-100K      | 10-22/s    | ~18 min    |
 * |  9   | 100K-300K     | 20-43/s    | ~25 min    |
 * | 10   | 300K-1M       | 17-68/s    | ~35 min    |
 * | 11   | 500K-10M      | 22-110/s   | ~45 min    |
 */

import { TIER_01_BUILDINGS } from './tier01-basic';
import { TIER_02_BUILDINGS } from './tier02-stone';
import { TIER_03_BUILDINGS } from './tier03-agriculture';
import { TIER_04_BUILDINGS } from './tier04-metallurgy';
import { TIER_05_BUILDINGS } from './tier05-knowledge';
import { TIER_06_BUILDINGS } from './tier06-industry';
import { TIER_07_BUILDINGS } from './tier07-commerce';
import { TIER_08_BUILDINGS } from './tier08-advanced';
import { TIER_09_BUILDINGS } from './tier09-science';
import { TIER_10_BUILDINGS } from './tier10-magic';
import { TIER_11_BUILDINGS } from './tier11-wonder';

// Combined buildings object
export const buildings = {
  ...TIER_01_BUILDINGS,
  ...TIER_02_BUILDINGS,
  ...TIER_03_BUILDINGS,
  ...TIER_04_BUILDINGS,
  ...TIER_05_BUILDINGS,
  ...TIER_06_BUILDINGS,
  ...TIER_07_BUILDINGS,
  ...TIER_08_BUILDINGS,
  ...TIER_09_BUILDINGS,
  ...TIER_10_BUILDINGS,
  ...TIER_11_BUILDINGS,
};

// Re-export individual tiers for selective imports
export {
  TIER_01_BUILDINGS,
  TIER_02_BUILDINGS,
  TIER_03_BUILDINGS,
  TIER_04_BUILDINGS,
  TIER_05_BUILDINGS,
  TIER_06_BUILDINGS,
  TIER_07_BUILDINGS,
  TIER_08_BUILDINGS,
  TIER_09_BUILDINGS,
  TIER_10_BUILDINGS,
  TIER_11_BUILDINGS,
};

// === HELPER FUNCTIONS ===

/**
 * Get all buildings of a specific tier
 */
export const getBuildingsByTier = (tier) =>
  Object.values(buildings).filter(b => b.tier === tier);

/**
 * Get total building count
 */
export const getBuildingCount = () =>
  Object.keys(buildings).length;

/**
 * Get buildings unlocked by a specific tech
 */
export const getBuildingsUnlockedByTech = (techId) =>
  Object.values(buildings).filter(b => b.unlockCondition?.tech === techId);

/**
 * Get all starter buildings (no unlock condition)
 */
export const getStarterBuildings = () =>
  Object.values(buildings).filter(b => !b.unlockCondition);

/**
 * Get building tier stats
 */
export const getTierStats = () => {
  const stats = {};
  for (let tier = 1; tier <= 11; tier++) {
    stats[tier] = getBuildingsByTier(tier).length;
  }
  return stats;
};

export default buildings;
